import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import { AppColors } from "../../constants/AppColors";
import { StatisticsBreakdownItem } from "../../models/ResponseModels/StatisticsSummaryResponse";
import { formatMoney, getBreakdownTitle } from "./statisticsHelpers";

interface AnalyticsBreakdownCardProps {
  items: StatisticsBreakdownItem[];
  t: (key: string, values?: Record<string, string | number>) => string;
}

const AnalyticsBreakdownCard = ({ items, t }: AnalyticsBreakdownCardProps) => {
  return (
    <Card sx={{ borderRadius: 3, mb: 2 }}>
      <CardContent>
        <Typography sx={{ fontWeight: 950, mb: 2 }}>
          {t("statistics.performanceBreakdown")}
        </Typography>

        {items.length === 0 ? (
          <Typography color="text.secondary">
            {t("statistics.noStatistics")}
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            {items.map((item, index) => {
              const profitPositive = item.net_profit >= 0;

              return (
                <Box
                  key={`${getBreakdownTitle(item)}-${item.currency}-${index}`}
                  sx={{
                    p: 1.75,
                    borderRadius: 3,
                    border: `1px solid ${AppColors.borderSoft}`,
                    bgcolor: AppColors.surfaceSoft,
                  }}
                >
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={1.5}
                    sx={{
                      justifyContent: "space-between",
                      alignItems: { xs: "flex-start", md: "center" },
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1.25}
                      sx={{ alignItems: "center", minWidth: 0 }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          bgcolor: AppColors.primarySoft,
                          color: AppColors.primary,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 950,
                          flexShrink: 0,
                        }}
                      >
                        {index + 1}
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 950 }} noWrap>
                          {getBreakdownTitle(item)}
                        </Typography>

                        <Typography color="text.secondary" variant="body2">
                          {t("statistics.breakdownMeta", {
                            bookings: item.booking_count,
                            nights: item.booked_nights,
                          })}
                        </Typography>
                      </Box>
                    </Stack>

                    <Typography
                      sx={{
                        fontWeight: 950,
                        color: profitPositive ? "#16a34a" : AppColors.danger,
                      }}
                    >
                      {formatMoney(item.net_profit, item.currency)}
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      mt: 1.5,
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(3, 1fr)",
                      },
                      gap: 1,
                    }}
                  >
                    {[
                      [
                        t("statistics.revenue"),
                        formatMoney(item.revenue, item.currency),
                      ],
                      [
                        t("statistics.expenses"),
                        formatMoney(item.total_expenses, item.currency),
                      ],
                      [
                        t("statistics.margin"),
                        `${Math.round(item.profit_margin_percentage || 0)}%`,
                      ],
                    ].map(([label, value]) => (
                      <Box
                        key={label}
                        sx={{
                          p: 1,
                          borderRadius: 2,
                          bgcolor: AppColors.card,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: AppColors.textMuted,
                            fontWeight: 700,
                          }}
                        >
                          {label}
                        </Typography>

                        <Typography sx={{ fontWeight: 900 }}>
                          {value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default AnalyticsBreakdownCard;
